"use client";

import clsx from "clsx";
import type { ComponentProps } from "react";

import { useImageMaskRef } from "@shared/hooks/use-image-mask-ref";
import { BrandIcon } from "@shared/ui/brand-icon";
import { Image } from "@shared/ui/image";
import { Link } from "@shared/ui/link";
import { StatusBadge } from "@shared/ui/status-badge";
import { Body, Heading } from "@shared/ui/typography";
import { WorkingHours } from "@shared/ui/working-hours";
import { formatLength } from "@shared/utils/format-trail-info";
import { getStatusLabel } from "@shared/utils/get-status-label";

import type { TrailPageType } from "@/_pages/single-trail/model/types";

import s from "./cable-way-card.module.scss";

export type CableWayCardProps = ComponentProps<"div"> & {
  className?: string;
  trail: TrailPageType;
};

export const CableWayCard = (props: CableWayCardProps) => {
  const { className, trail } = props;

  const elevation = trail.length ? formatLength(trail.length, "м") : null;

  const $imageRef = useImageMaskRef();

  return (
    <article className={clsx(s.root, className)}>
      <div className={s.content}>
        <div className={s.header}>
          <div className={s.top}>
            <StatusBadge variant={trail.trailStatus}>
              {getStatusLabel(trail.trailStatus)}
            </StatusBadge>

            {elevation && (
              <Body size="primary" weight="semiBold" className={s.elevation}>
                {elevation}
              </Body>
            )}
          </div>
          {trail?.title != null && trail.title !== "" && (
            <Heading level={6} tag="h2" className={s.title}>
              {trail.title}
            </Heading>
          )}
        </div>
        {trail?.image?.url && (
          <div className={s.imageWrapper}>
            <Image
              src={trail.image.url}
              alt={
                trail.image?.alternativeText ?? trail?.title ?? "trail image"
              }
              className={s.image}
              height="100%"
              width="100%"
              objectFit="cover"
              ref={$imageRef}
            />
          </div>
        )}
        <div className={s.footer}>
          <WorkingHours
            workingHours={trail.workingHours}
            ascentTime={trail.ascentTime}
            descentTime={trail.descentTime}
            className={s.workingHours}
          />
          {trail.icon?.icon?.name && (
            <BrandIcon
              name={trail.icon.icon.name}
              className={s.icon}
              fillColor={trail.icon.fillColor}
              strokeColor={trail.icon.strokeColor}
              svg_code={trail.icon?.uicon?.svg_code}
            />
          )}
        </div>
      </div>
      <Link
        variant="block"
        href={`/trails/${trail.uid}`}
        className={s.link}
        aria-label={trail?.title ?? undefined}
      >
        &nbsp;
      </Link>
    </article>
  );
};

CableWayCard.displayName = "CableWayCard";
